import { Outlet, createRootRoute } from '@tanstack/react-router'
import { Toaster } from 'sonner'
import * as Sentry from '@sentry/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { FileQuestion } from 'lucide-react'

function ErrorFallback({
  error: _error,
  eventId,
}: {
  error: unknown
  eventId?: string
}) {
  return (
    <div className="flex flex-col items-center justify-center min-h-svh p-8 bg-background">
      <div className="max-w-md w-full space-y-6 text-center">
        <div className="space-y-2">
          <h1 className="text-2xl font-semibold text-destructive">
            Something went wrong
          </h1>
          <p className="text-muted-foreground">
            We've been notified and are working on a fix. Please try refreshing
            the page.
          </p>
        </div>

        {eventId && (
          <div className="text-xs text-muted-foreground bg-muted p-3 rounded-md">
            <p className="font-medium mb-1">Error ID:</p>
            <p className="font-mono">{eventId}</p>
          </div>
        )}

        <div className="flex gap-3 justify-center">
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Reload Page
          </button>
          {eventId && (
            <button
              onClick={() =>
                Sentry.showReportDialog({ eventId: eventId || undefined })
              }
              className="px-4 py-2 border border-input rounded-md hover:bg-accent transition-colors"
            >
              Report Issue
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function RootComponent() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
      <Outlet />
      <Toaster richColors position="top-center" />
    </Sentry.ErrorBoundary>
  )
}

function NotFoundComponent() {
  return (
    <div className="min-h-svh bg-background flex items-center justify-center p-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center space-y-4">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-amber-100">
            <FileQuestion className="h-10 w-10 text-amber-700" />
          </div>
        </div>

        {/* Not Found Card */}
        <Card className="border-amber-300 bg-amber-50/50">
          <CardHeader>
            <div className="flex items-center gap-3">
              <FileQuestion className="h-6 w-6 text-amber-600" />
              <div>
                <CardTitle className="text-amber-800">Page Not Found</CardTitle>
                <CardDescription>We couldn't find what you're looking for</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              The page you're trying to access doesn't exist or the link may be incorrect.
            </p>
            <p className="text-sm text-muted-foreground">
              Please reach out to the vendor who sent you this link to request a new one.
            </p>
          </CardContent>
        </Card>

        {/* Additional Help Text */}
        <p className="text-center text-xs text-dim">
          Need help? Contact the business that sent you this link.
        </p>
      </div>
    </div>
  )
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
})
